import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Table, Input, Space, Button, DatePicker, Segmented, Row, Col, Card, Empty, Select, Tag, InputNumber, Modal, Dropdown, message, Tooltip, Badge, Switch, Spin } from 'antd';
import { Pie as AntPie, Column as AntColumn, Bar as AntBar, Line as AntLine, Area as AntArea, Scatter as AntScatter, Rose as AntRose } from '@ant-design/charts';
import {
	SearchOutlined,
	FilterFilled,
	PieChartOutlined,
	BarChartOutlined,
	LineChartOutlined,
	DotChartOutlined,
	AreaChartOutlined,
	RiseOutlined,
	AppstoreOutlined,
	SettingOutlined,
	PlusOutlined,
	ClearOutlined,
	EyeOutlined,
	DownloadOutlined,
	ExpandOutlined,
	CompressOutlined,
	DeleteOutlined,
	CalendarOutlined,
	NumberOutlined,
	FilterOutlined,
	DashboardOutlined,
	ThunderboltOutlined,
	TrophyOutlined,
	TeamOutlined,
	ClockCircleOutlined,
	CheckCircleOutlined,
	SyncOutlined,
	FullscreenOutlined,
	FullscreenExitOutlined,
	UpOutlined,
	DownOutlined,
	TableOutlined
} from '@ant-design/icons';
import moment from 'moment';
import _ from "underscore";
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';
import Paginator from './Paginator.js';
import GraphView from './GraphView.jsx';
import { formConditionsHandler } from '../../../../utils/formsHelper.js';
import { safeMoment, toDatePickerValue, toISOStringSafe } from '../../../../utils/safeDate.js';
// import { getLocalFormattedDate } from '../../../../utils/getLocalFormattedDate';

const { Option } = Select;

const getLocalDateTime = (date) => {
	if (!date) return '';
	try {
		const parsed = safeMoment(date);
		if (!parsed.isValid()) return '';
		const utcDate = moment.utc(parsed.toDate()).toDate();
		return moment(utcDate).local().format('YYYY-MM-DD, hh:mm a');
	} catch (e) {
		return String(date);
	}
};

const TableComponent = (props) => {
	// Removed unused RangePicker destructure
	const {
		config = {},
		loader,
		selectType = "checkbox",
		formItems = [],
		locale,
		showPagination,
		onViewModeChange,
		graphData,
		spaceId,
		viewId,
		onRowClick,
		rowClickable,
		currentPage,
		pageSize,
		showCustomPagination,
		total,
		onPageChange,
		onRequireSaveView,
		viewMode: propViewMode,
		data: propData,
		onChange,
		selectable,
		rowKey,
		selectedRowKeys: propSelectedRowKeys,
		onSelectionChange,
		editable = false,
		editedRows = {},
		onCellChange,
		formConditions = [],
		selectionLoadingKeys = [],
		getCheckboxProps,
		hideViewToggle = false,
		availableColumns = [],
		userInfo,
	} = props;
	const { t } = useTranslation();
	const [currentActiveTable, setCurrentActiveTable] = useState({})
	const [currentColumns, setCurrentColumns] = useState([])
	// const [loading, setLoading] = useState(loader || false);
	const [data, setData] = useState([])
	const [internalViewMode, setInternalViewMode] = useState('table'); // 'table' | 'graph'
	const viewMode = propViewMode !== undefined ? propViewMode : internalViewMode; // Use prop if provided, otherwise internal state
	const startIndex = (currentPage - 1) * pageSize;
	const endIndex = Math.min(startIndex + pageSize, total);

	// Create a setter function that updates the appropriate state/callback
	const setViewMode = (newMode) => {
		if (propViewMode !== undefined && onViewModeChange) {
			// If viewMode is controlled by parent, use the callback
			onViewModeChange(newMode);
		} else {
			// If viewMode is internal, update internal state and notify parent
			setInternalViewMode(newMode);
			onViewModeChange && onViewModeChange(newMode);
		}
	};

	const [searchText, setSearchText] = useState('')
	const [searchedColumn, setSearchedColumn] = useState('');
	const searchInput = useRef(null);
	const [internalSelectedRowKeys, setInternalSelectedRowKeys] = useState([]);
	const selectedRowKeys = propSelectedRowKeys !== undefined ? propSelectedRowKeys : internalSelectedRowKeys;

	let tablePagination = showPagination !== undefined ? showPagination : true

	// Sync selected rows & data from props only in table mode; guard to avoid redundant sets
	useEffect(() => {
		if (viewMode !== 'table') return;
		const nextData = Array.isArray(props.data) ? props.data : [];
		// setData only if reference changed to avoid extra renders
		setData((prev) => (prev === nextData ? prev : nextData));
		// compute next selected keys and shallow-compare before setting
		const nextKeys = nextData.filter((item) => item?.chosen).map((item) => {
			if (rowKey) {
				return typeof rowKey === 'function' ? rowKey(item) : item[rowKey];
			}
			return item?.key;
		});
		setInternalSelectedRowKeys((prev) => {
			if (!Array.isArray(prev) || prev.length !== nextKeys.length) return nextKeys;
			for (let i = 0; i < prev.length; i++) { if (prev[i] !== nextKeys[i]) return nextKeys; }
			return prev;
		});
	}, [props.data, currentActiveTable.id, viewMode])

	useEffect(() => {
		const nextTable = (config && config.type === 'multi_table') ? config[config.default] : config;
		const nextCols = (nextTable && Array.isArray(nextTable.columns)) ? nextTable.columns : [];
		setCurrentActiveTable((prev) => (prev === nextTable ? prev : nextTable));
		setCurrentColumns((prev) => (prev === nextCols ? prev : nextCols));
	}, [config])

	// Removed redundant effect that depended on `props` (object identity changes every render) leading to potential update depth issues.

	// swap in graphData when provided and viewMode is graph
	useEffect(() => {
		if (viewMode === 'graph' && Array.isArray(graphData)) {
			setData((prev) => (prev === graphData ? prev : graphData));
		} else if (viewMode === 'table' && Array.isArray(props.data)) {
			setData((prev) => (prev === props.data ? prev : props.data));
		}
	}, [viewMode, graphData, props.data])

	const handleSearch = (selectedKeys, confirm, dataIndex, type) => {
		confirm();
		setSearchText(selectedKeys[0]);
		setSearchedColumn(dataIndex);
	};

	const handleReset = (clearFilters) => {
		clearFilters();
		setSearchText('');
	};

	const getColumnSearchProps = (dataIndex, type) => ({
		filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
			<div
				style={{
					padding: 8,
				}}
				onKeyDown={(e) => e.stopPropagation()}
			>
				{
					type === 'input' ?
						<Input
							ref={searchInput}
							placeholder={`${t("Search")} ${dataIndex}`}
							value={selectedKeys[0]}
							onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
							onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex, type)}
							style={{
								marginBottom: 8,
								display: 'block',
							}}
						/>
						:
						<DatePicker value={selectedKeys[0]} onChange={(date) => setSelectedKeys(date ? [date] : [])} style={{
							marginBottom: 8,
							display: 'block',
						}} />
				}
				<Space>
					<Button
						type="primary"
						onClick={() => handleSearch(selectedKeys, confirm, dataIndex, type)}
						icon={<SearchOutlined />}
						size="small"
						style={{
							width: 90,
						}}
					>
						{t("Search")}
					</Button>
					<Button
						onClick={() => clearFilters && handleReset(clearFilters)}
						size="small"
						style={{
							width: 90,
						}}
					>
						{t("Reset")}
					</Button>
				</Space>
			</div>
		),
		filterIcon: (filtered) => (
			type === 'input' ?
				<SearchOutlined
					style={{
						color: filtered ? '#1677ff' : undefined,
					}}
				/>
				: <FilterFilled />
		),
		onFilter: (value, record) => {
			if (type === 'date') {
				// Both sides can be a dayjs (the picker's value, or a stored form
				// value), which plain moment() reads as Invalid date.
				const cell = safeMoment(record[dataIndex])
				const target = safeMoment(value)
				if (!cell.isValid() || !target.isValid()) return false
				return cell.format("DD-MM-YYYY") === target.format("DD-MM-YYYY")
			} else {
				const cell = record[dataIndex]
				if (cell === undefined || cell === null) return false
				return cell.toString().toLowerCase().includes(value.toLowerCase())
			}
		},
		filterDropdownProps: {
			onOpenChange: (open) => {
				if (open) setTimeout(() => searchInput.current?.select(), 100);
			}
		},
		render: (text) =>
			(searchedColumn === dataIndex && type === 'input') ? (
				<Highlighter
					highlightStyle={{
						backgroundColor: '#ffc069',
						padding: 0,
					}}
					searchWords={[searchText]}
					autoEscape
					textToHighlight={text ? text.toString() : ''}
				/>
			) : (
				type === "input" ? text : getLocalDateTime(text)
			),
	});

	const setRowSelection = (newKeys, selectedRows) => {
		setInternalSelectedRowKeys(newKeys)
		if (onSelectionChange) {
			onSelectionChange(newKeys, selectedRows)
		}
		if (props.getSelectedData) {
			props.getSelectedData(selectedRows)
		}
	}
	// const handleMenuClick = (e) => {
	// };

	// const handleColumnSelection = (selectedOption) => {
	// 	if (selectedOption && selectedOption.length > 0) {
	// 		let filteredColumns = _.filter(columns, c => selectedOption.includes(c.title))
	// 		setCurrentColumns(filteredColumns)
	// 	} else {
	// 		setCurrentColumns(columns)
	// 	}
	// }

	/* Unused UI props retained for future feature wiring
		const availableColumnProps = {
			options: _.map(columns, rec => ({ label: rec.title, key: rec.title, value: rec.title })),
			onChange: handleColumnSelection,
		};
	
		const exportProps = {
			items: [{ label: "CSV", key: "CSV" }, { label: "PDF", key: "PDF" }],
			onClick: handleMenuClick,
		};
	*/

	// System columns that should not be editable (includes default columns from DocumentsList)
	const systemColumns = ['id', 'key', 'createdAt', 'createdOn', 'updatedAt', 'updatedOn', 'createdBy', 'updatedBy', 'tenantId', 'userId', 'caseId', 'status', 'currentCycle', 'requestId'];

	const tableColumns = currentColumns.map((item) => {
		let dataIndex = item.dataIndex;
		let dataKey = item.dataKey;
		let isDate = item.isDate;
		let valueMapping = item.valueMapping;

		let availableValues = _.map(data, rec => {
			let text = (dataKey && rec[dataKey]) ? rec[dataKey][dataIndex] : rec[dataIndex]
			let value = (dataKey && rec[dataKey]) ? rec[dataKey][dataIndex] : rec[dataIndex]
			if (isDate) {
				text = getLocalDateTime(text)
			}
			if (valueMapping && valueMapping[text]) {
				text = valueMapping[text]
			}
			if (valueMapping && valueMapping[value]) {
				value = valueMapping[value]
			}
			return ({
				text: text,
				value: value
			})
		})

		availableValues = availableValues.filter(val => val.text)
		availableValues = _.uniq(availableValues, a => a.text)

		// Check if this column is editable (editable prop OR row is selected)
		const isEditableColumn = !systemColumns.includes(dataIndex) && !item.no_edit;

		let updatedItem =
		{
			...item,
			//  ellipsis: true,
			// A column can declare its own filter list (e.g. Status, which offers every
			// workflow step); otherwise fall back to the values present in the data.
			filters: !item.no_filter && (Array.isArray(item.filters) ? item.filters : availableValues),
			onFilter: (value, record) => {
				let rec = dataKey ? record[dataKey] : record;
				let testValue = valueMapping ? valueMapping[rec[dataIndex]] : rec[dataIndex]
				let _value = false
				if (testValue && typeof testValue == "string") {
					_value = testValue.indexOf(value) === 0
				} else if (testValue && typeof testValue == "number") {
					_value = `${testValue}`.indexOf(value) === 0
				}
				return _value
			}
		}

		// Add inline editing render function for editable columns
		if (isEditableColumn) {
			const originalRender = updatedItem.render;

			// Find the corresponding form item to get form type and options
			const formItem = formItems.find(item => item.id === dataIndex);
			const formType = formItem?.formType || 'textField';
			const formOptions = formItem?.options || [];

			updatedItem.render = (text, record, index) => {
				const recordKey = rowKey ? (typeof rowKey === 'function' ? rowKey(record) : record[rowKey]) : record.key;
				const isRowSelected = selectedRowKeys.includes(recordKey);

				// Check form conditions for this field
				let conditionalActions = [];
				let isFieldVisible = true;
				let isFieldEditable = true; // Default to editable when row is selected
				let isFieldMandatory = false;
				let hasConditionsForField = false;

				if (formConditions && formConditions.length > 0 && formItem) {
					// Check if there are any conditions that target this specific field
					hasConditionsForField = formConditions.some(condition =>
						condition.actions && condition.actions.some(action => action.field === formItem.id)
					);

					// Get current workflow node ID from record's originalWorkflow
					// Conditions compare against node IDs, not status names
					let currentWorkflowNodeId = "";
					const statusName = record.status || "";
					const workflowNodes = record.originalWorkflow?.nodes || [];

					// Find the node that matches the current status name
					const currentNode = workflowNodes.find(node => node?.data?.name === statusName);
					if (currentNode) {
						currentWorkflowNodeId = currentNode.id || "";
					} else {
						// Fallback: try to find the default node if status doesn't match any node name
						const defaultNode = workflowNodes.find(node => node?.type === "default");
						if (defaultNode) {
							currentWorkflowNodeId = defaultNode.id || "";
						}
					}

					// Prepare form values from row data (merge value object if present)
					const formValues = { ...record, ...(record.value || {}) };

					// Call formConditionsHandler to check conditions for this field
					const conditionalChanges = formConditionsHandler(
						formConditions,
						formValues,
						formItem,
						formItems,
						currentWorkflowNodeId, // Pass node ID instead of status name
						null // userInfo not needed for editability check
					);

					// Extract action strings from conditional changes (similar to Wrapper.jsx line 46)
					if (conditionalChanges && conditionalChanges.length > 0) {
						conditionalActions = conditionalChanges.map(condition =>
							typeof condition === "string" ? condition : condition.action
						);

						// Handle SHOW/HIDE conditions
						if (formItem.hideInitially) {
							// Field is initially hidden, only show if "show" action exists
							isFieldVisible = conditionalActions.includes("show");
						} else {
							// Field is initially visible, hide if "hide" action exists
							isFieldVisible = !conditionalActions.includes("hide");
						}

						// Handle MANDATORY conditions
						isFieldMandatory = conditionalActions.includes("mandatory");
					}
				}

				// --- Calculate Editability (Logic matched with Wrapper.jsx) ---

				// 1. Default editability based on widget definition
				// If widget is readOnly by default, it starts as NOT editable
				if (formItem?.readOnly) {
					isFieldEditable = false;
				}

				// 2. Check "Freeze Fields" (Fields from previous workflow steps should be read-only)
				// Replicating logic from RequestForm.jsx lines 83-93
				let isFrozen = false;
				const workflowHistory = record.workflow || [];
				if (workflowHistory.length > 0 && record.status !== "draft") {
					// Loop through all steps EXCEPT the last one (current active step)
					// If the field was present in any previous step's formValues, it is frozen
					for (let i = 0; i < workflowHistory.length - 1; i++) {
						const step = workflowHistory[i];
						// Use formItem.id (the field's UUID) not dataIndex (column name)
						if (step.formValues && formItem && step.formValues[formItem.id] !== undefined) {
							isFrozen = true;
							break;
						}
					}
				}
				if (isFrozen) {
					isFieldEditable = false;
				}

				// 3. Apply Conditions (Conditions can OVERRIDE default readOnly and Freeze state)
				if (conditionalActions.includes("editable")) {
					// If condition explicitly says "editable", it becomes editable (overrides all above)
					isFieldEditable = true;
				} else if (hasConditionsForField) {
					// If conditions EXIST for this field but "editable" was NOT returned, 
					// it implies the condition for editability failed, so force read-only.
					isFieldEditable = false;
				}

				// If field is hidden based on conditions, don't render anything (or show dash)
				if (!isFieldVisible) {
					return <span style={{ color: '#999' }}>-</span>;
				}

				if (isRowSelected) {
					// If field is not editable based on conditions, show read-only text
					if (!isFieldEditable) {
						return originalRender ? originalRender(text, record, index) : text;
					}

					// Get the edited value or use the original
					const rowEdits = editedRows[recordKey] || {};
					const editedValue = rowEdits[dataIndex];
					const displayValue = editedValue !== undefined ? editedValue : text;

					// Render appropriate input based on form type
					switch (formType) {
						case 'dropdownField':
						case 'radioField':
							return (
								<Select
									size="small"
									value={displayValue}
									onChange={(value) => onCellChange && onCellChange(record, dataIndex, value)}
									style={{ width: '100%' }}
									placeholder={t('Select...')}
								>
									{formOptions.map((opt) => (
										<Option key={opt.value} value={opt.value}>
											{opt.label}
										</Option>
									))}
								</Select>
							);

						case 'multipleChoiceField':
							return (
								<Select
									mode="multiple"
									size="small"
									value={Array.isArray(displayValue) ? displayValue : displayValue ? [displayValue] : []}
									onChange={(value) => onCellChange && onCellChange(record, dataIndex, value)}
									style={{ width: '100%' }}
									placeholder={t('Select...')}
								>
									{formOptions.map((opt) => (
										<Option key={opt.value} value={opt.value}>
											{opt.label}
										</Option>
									))}
								</Select>
							);

						case 'numberField':
							return (
								<InputNumber
									size="small"
									value={displayValue}
									onChange={(value) => onCellChange && onCellChange(record, dataIndex, value)}
									style={{ width: '100%' }}
									placeholder={t('Enter number...')}
								/>
							);

						case 'dateField':
							return (
								<DatePicker
									size="small"
									value={toDatePickerValue(displayValue)}
									onChange={(date) => onCellChange && onCellChange(record, dataIndex, date ? date.format('YYYY-MM-DD') : null)}
									style={{ width: '100%' }}
									placeholder={t('Select date...')}
								/>
							);

						case 'checkboxField':
							return (
								<Switch
									size="small"
									checked={!!displayValue}
									onChange={(checked) => onCellChange && onCellChange(record, dataIndex, checked)}
								/>
							);

						case 'textareaField':
							return (
								<Input.TextArea
									size="small"
									value={displayValue}
									onChange={(e) => onCellChange && onCellChange(record, dataIndex, e.target.value)}
									style={{ width: '100%' }}
									rows={2}
									placeholder={t('Enter text...')}
								/>
							);

						case 'textField':
						default:
							return (
								<Input
									size="small"
									value={displayValue}
									onChange={(e) => onCellChange && onCellChange(record, dataIndex, e.target.value)}
									style={{ width: '100%' }}
									placeholder={t('Enter text...')}
								/>
							);
					}
				}

				// Return original render if not selected, or just the text
				return originalRender ? originalRender(text, record, index) : text;
			};
		}

		if (item.custom_search) {
			updatedItem = { ...updatedItem, ...getColumnSearchProps(item.dataIndex, 'input') }
		}
		if (item.date_filter) {
			updatedItem = { ...updatedItem, ...getColumnSearchProps(item.dataIndex, 'date') }
			// updatedItem = { ...updatedItem, ...getColumnDateSearchProps(item.dataIndex) }
		}
		return (updatedItem)
	});

	const isSelectable = selectable !== undefined ? selectable : config.selectable !== false;

	const tableProps = {
		loading: loader,
		className: `custom-table ${config.template} ${config.no_body ? "no_body" : ""}`,
		rowKey: rowKey || "key",
		...(isSelectable && {
			rowSelection: {
				selectedRowKeys,
				onChange: (newRowKeys, newRows) => setRowSelection(newRowKeys, newRows),
				type: selectType,
				...(getCheckboxProps && { getCheckboxProps }),
				renderCell: (checked, record, index, originNode) => {
					const recordKey = rowKey ? (typeof rowKey === 'function' ? rowKey(record) : record[rowKey]) : record.key;
					const isLoading = selectionLoadingKeys.includes(recordKey);
					if (!isLoading) return originNode;
					const disabledNode = React.isValidElement(originNode)
						? React.cloneElement(originNode, { disabled: true })
						: originNode;
					return (
						<Space size={6}>
							<Spin size="small" />
							{disabledNode}
						</Space>
					);
				}
			}
		})
	};

	const handleChange = (pagination, filters, sorters, extra) => {
		if (onChange) {
			onChange(pagination, filters, sorters, extra);
		}
	}

	// ----- Graphical view helpers -----

	return (
		<>
			{/* Top toolbar with mode toggle (hidden when the parent renders its own view tabs) */}
			<div className="flex items-center justify-between mb-3 absolute top-0 right-0 z-10" style={hideViewToggle ? { display: 'none' } : undefined}>
				<div className="text-sm text-gray-500">{t('')}</div>
				<Segmented
					options={[]}
					value={viewMode}
					onChange={(val) => {
						setViewMode(val);
						onViewModeChange && onViewModeChange(val);
					}}
				/>
			</div>

			{viewMode === 'table' && (
				<Table
					{...tableProps}
					locale={locale}
					columns={tableColumns}
					dataSource={data}
					onChange={handleChange}
					tableLayout='fixed'
					pagination={tablePagination}

					scroll={{ x: 'max-content' }}
					// pagination={{
					// 	position: ["bottomLeft"],
					// }}
					onRow={(record, rowIndex) => {
						if (rowClickable) {
							return {
								onClick: (event) => {
									// Only navigate if clicking on the Request ID cell (caseId column)
									// Check if the click target is within the first cell (Request ID column)
									const target = event.target;
									const cell = target.closest('td');

									// Get all cells in the row
									const row = cell?.closest('tr');
									if (row && cell) {
										const cells = Array.from(row.querySelectorAll('td'));
										const cellIndex = cells.indexOf(cell);

										// Check if clicking on interactive elements (input, link, button, checkbox)
										const isInteractive = target.closest('a, button, input, .ant-checkbox, .ant-checkbox-wrapper');

										// Only navigate if clicking on the Request ID column (first data cell after checkbox)
										// and not clicking on interactive elements
										if (cellIndex === 1 && !isInteractive) {
											onRowClick(record);
										}
									}
								}
							}
						}
					}}
				/>
			)}

			{viewMode === 'graph' && (
				<GraphView
					data={data}
					columns={currentColumns}
					availableColumns={availableColumns}
					spaceId={spaceId || config.spaceId}
					viewId={viewId || config.viewId}
					userInfo={userInfo}
				/>
			)}
			{
				viewMode === 'table' && showCustomPagination && (
					<div className='flex justify-between items-center'>
						<div className='text-[#000000a9]'>
							{t('Showing')} {startIndex + 1} to {endIndex} of {total} requests
						</div>
						<Paginator pageSize={pageSize} currentPage={currentPage} onChange={onPageChange} total={total} />
					</div>
				)
			}
		</>
	);
};

// Legacy multi-panel dashboard (superseded by GraphView; kept for reference)
// eslint-disable-next-line no-unused-vars
const GraphicalDashboard = ({ data = [], columns = [] }) => {
	const { t } = useTranslation();
	// Graph mode: Overview (preset charts) or Custom (builder)
	const { RangePicker } = DatePicker;
	const [graphMode, setGraphMode] = useState('overview'); // 'overview' | 'custom'
	const [density, setDensity] = useState('compact'); // 'compact' | 'comfortable'

	// Builder state
	const [builder, setBuilder] = useState({
		chartType: 'Pie', // Pie | Donut | Bar | Column | Line | Area | Scatter | Rose
		dimension: [], // multiple categorical fields
		series: [], // multiple categorical fields used to compose series label
		dateField: undefined,
		topN: 10,
		xField: undefined, // for Scatter
		yField: undefined, // for Scatter
	});
	const [dateRange, setDateRange] = useState([]);
	const [activeFilters, setActiveFilters] = useState({}); // { field: Set(values) }
	const [panels, setPanels] = useState([]); // array of saved charts
	const chartRefs = useRef(new Map()); // key -> plot instance for export
	const storageKey = 'ff_graph_dashboard_v1';

	// Viewer modal state
	const [viewer, setViewer] = useState({ open: false, title: '', columns: [], data: [] });

	// Derive field options from columns and data
	const fieldOptions = React.useMemo(() => {
		const fields = new Set();
		(columns || []).forEach((c) => c?.dataIndex && fields.add(c.dataIndex));
		// also include observed keys on data
		(data || []).slice(0, 50).forEach((rec) => {
			Object.keys(rec || {}).forEach((k) => fields.add(k));
		});
		const sample = (data && data.length > 0) ? data[0] : {};
		const toOpts = (arr) => arr.map((f) => ({ label: f, value: f }));
		const all = Array.from(fields);
		const categorical = all.filter((f) => {
			const val = sample?.[f];
			return typeof val === 'string';
		});
		const numeric = all.filter((f) => typeof sample?.[f] === 'number');
		const dateLike = all.filter((f) => sample?.[f] && safeMoment(sample[f]).isValid());
		// sensible defaults
		const dimDefault = categorical.includes('status') ? 'status' : (categorical.includes('createdBy') ? 'createdBy' : categorical[0]);
		const dateDefault = dateLike.includes('createdOn') ? 'createdOn' : (dateLike.includes('createdAt') ? 'createdAt' : dateLike[0]);
		return {
			categorical: toOpts(categorical),
			numeric: toOpts(numeric),
			dates: toOpts(dateLike),
			defaults: { dimDefault, dateDefault },
		};
	}, [columns, data]);

	// Ensure selected values in multi-selects are visible regardless of inherited color
	const tagItemRender = React.useCallback((props) => {
		const { label, closable, onClose } = props;
		const onPreventMouseDown = (e) => {
			e.preventDefault();
			e.stopPropagation();
		};
		return (
			<Tag color="blue" onMouseDown={onPreventMouseDown} closable={closable} onClose={onClose} className="m-0">
				{label}
			</Tag>
		);
	}, []);

	// Initialize builder defaults when options change
	useEffect(() => {
		setBuilder((prev) => ({
			...prev,
			dimension: (prev.dimension && prev.dimension.length > 0) ? prev.dimension : (fieldOptions.defaults.dimDefault ? [fieldOptions.defaults.dimDefault] : []),
			dateField: prev.dateField ?? fieldOptions.defaults.dateDefault,
			xField: prev.xField ?? (fieldOptions.numeric?.[0]?.value),
			yField: prev.yField ?? (fieldOptions.numeric?.[1]?.value || fieldOptions.numeric?.[0]?.value),
		}));
	}, [fieldOptions]);

	// Load from localStorage once
	useEffect(() => {
		try {
			const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
			if (saved) {
				if (Array.isArray(saved.panels)) setPanels(saved.panels);
				if (saved.builder) setBuilder((b) => ({ ...b, ...saved.builder }));
				if (Array.isArray(saved.dateRange)) setDateRange(saved.dateRange.map((d) => (d ? toDatePickerValue(d) : d)));
				if (saved.activeFilters) {
					const obj = {};
					Object.entries(saved.activeFilters).forEach(([k, arr]) => obj[k] = new Set(arr));
					setActiveFilters(obj);
				}
				if (saved.graphMode) setGraphMode(saved.graphMode);
			}
		} catch (e) {
			// ignore parse errors
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Persist to localStorage on change (no backend writes)
	useEffect(() => {
		const serializableFilters = {};
		Object.entries(activeFilters).forEach(([k, set]) => serializableFilters[k] = Array.from(set || []));
		const payload = {
			panels,
			builder,
			dateRange: dateRange?.map((d) => (d ? toISOStringSafe(d, null) : d)) || [],
			activeFilters: serializableFilters,
			graphMode,
		};
		try { localStorage.setItem(storageKey, JSON.stringify(payload)); } catch { }
	}, [panels, builder, dateRange, activeFilters, graphMode]);

	// Apply activeFilters and dateRange to raw data
	const filteredData = React.useMemo(() => {
		let out = Array.isArray(data) ? data.slice() : [];
		// date range
		if (builder.dateField && dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
			const [start, end] = dateRange;
			out = out.filter((rec) => {
				const d = safeMoment(rec[builder.dateField]);
				const from = safeMoment(start);
				const to = safeMoment(end);
				if (!d.isValid() || !from.isValid() || !to.isValid()) return false;
				return d.isSameOrAfter(from, 'day') && d.isSameOrBefore(to, 'day');
			});
		}
		// categorical filters
		Object.entries(activeFilters).forEach(([field, set]) => {
			if (!set || set.size === 0) return;
			if (field === '__date__' && builder.dateField) {
				out = out.filter((rec) => {
					const dv = safeMoment(rec[builder.dateField]);
					if (!dv.isValid()) return false;
					const dstr = dv.format('YYYY-MM-DD');
					return set.has(dstr);
				});
			} else {
				out = out.filter((rec) => set.has((rec[field] ?? 'Unknown')));
			}
		});
		return out;
	}, [data, builder.dateField, dateRange, activeFilters]);

	// addFilter removed; inline updates used in chart onReady handlers
	const removeFilter = (field, value) => {
		setActiveFilters((prev) => {
			const next = { ...prev };
			const s = new Set(next[field] || []);
			s.delete(value);
			if (s.size === 0) delete next[field]; else next[field] = s;
			return next;
		});
	};
	// no-op util removed to satisfy linter
	// Build a resolver for requester field based on table columns if available
	const buildRequesterResolver = () => {
		// Try to find a column that likely represents requester/requested by/created by
		const candidates = (columns || []).filter((c) => {
			const key = `${c.dataKey ?? ''}`.toLowerCase();
			const idx = `${c.dataIndex ?? ''}`.toLowerCase();
			const title = `${c.title ?? ''}`.toLowerCase();
			return (
				idx.includes('requester') || idx.includes('requestedby') || idx.includes('createdby') ||
				key.includes('requester') || key.includes('createdby') ||
				title.includes('requester') || title.includes('requested by') || title.includes('created by')
			);
		});
		if (candidates.length > 0) {
			const col = candidates[0];
			return (rec) => {
				const container = col.dataKey ? rec[col.dataKey] : rec;
				const val = container?.[col.dataIndex];
				if (!val) return undefined;
				if (typeof val === 'string') return val;
				if (typeof val === 'object') {
					// common shapes { name, email } or nested
					return val.name || val.fullName || val.email || val.username || undefined;
				}
				return `${val}`;
			};
		}
		// default resolver chain
		return (rec) => rec.requester ?? rec.createdBy?.name ?? rec.createdBy?.email ?? rec.createdBy ?? rec.owner ?? undefined;
	};

	const build = () => {
		// When there's no data, return a safe empty dataset shape to avoid runtime errors
		if (!filteredData || filteredData.length === 0) {
			return {
				status: [],
				requesters: [],
				cycles: [],
				dates: [],
			};
		}
		// reuse helper logic locally
		const statusCounts = {};
		const requesterCounts = {};
		const cycleCounts = {};
		const dateCounts = {};
		const resolveRequester = buildRequesterResolver();
		(filteredData || []).forEach((rec) => {
			const s = rec.status ?? rec.Status ?? 'Unknown';
			statusCounts[s] = (statusCounts[s] || 0) + 1;
			const cyc = (rec.currentCycle ?? rec.cycle ?? 'Unknown');
			cycleCounts[cyc] = (cycleCounts[cyc] || 0) + 1;
			const reqRaw = resolveRequester(rec);
			const req = reqRaw ?? 'Unknown';
			requesterCounts[req] = (requesterCounts[req] || 0) + 1;
			const created = rec.createdOn || rec.createdAt;
			const createdMoment = safeMoment(created);
			if (createdMoment.isValid()) {
				const d = createdMoment.format('YYYY-MM-DD');
				dateCounts[d] = (dateCounts[d] || 0) + 1;
			}
		});

		const toList = (obj, k) => Object.keys(obj).map((key) => ({ [k]: key, value: obj[key] }));
		let reqList = toList(requesterCounts, 'requester');
		// If we have real requesters besides 'Unknown', push Unknown to the end and keep it, or optionally remove it
		const hasReal = reqList.some((r) => r.requester !== 'Unknown');
		if (hasReal) {
			reqList = reqList.filter((r) => r.requester !== 'Unknown').sort((a, b) => b.value - a.value).slice(0, 10);
		}
		return {
			status: toList(statusCounts, 'status'),
			requesters: reqList,
			cycles: toList(cycleCounts, 'cycle'),
			dates: Object.keys(dateCounts).sort((a, b) => new Date(a) - new Date(b)).map((d) => ({ date: d, value: dateCounts[d] })),
		};
	};

	// Mode toggle + builder controls
	const ds = build() || { status: [], requesters: [], cycles: [], dates: [] };

	// Compact / collapsible controls state (reduces screen usage when not needed)
	const [controlsOpen, setControlsOpen] = useState(graphMode === 'custom');
	useEffect(() => {
		// Auto open when switching to custom mode; keep user choice for overview
		if (graphMode === 'custom') setControlsOpen(true);
	}, [graphMode]);

	// Enhanced Controls UI with better styling and icons
	const BuilderControls = (
		<Card size="small" className="shadow-sm p-2 border-blue-100" bodyStyle={{ padding: 12 }}>
			{/* Compact toolbar row */}
			<div className="flex flex-wrap items-center gap-3">
				<Segmented
					size="small"
					options={[
						{ label: <span className="flex items-center"><DashboardOutlined className="mr-1" />{t('Overview')}</span>, value: 'overview' },
						{ label: <span className="flex items-center"><SettingOutlined className="mr-1" />{t('Custom')}</span>, value: 'custom' },
					]}
					value={graphMode}
					onChange={setGraphMode}
				/>
				<Segmented
					size="small"
					options={[
						{ label: <span className="flex items-center"><CompressOutlined className="mr-1" />{t('Compact')}</span>, value: 'compact' },
						{ label: <span className="flex items-center"><ExpandOutlined className="mr-1" />{t('Comfort')}</span>, value: 'comfortable' },
					]}
					value={density}
					onChange={setDensity}
				/>
				<Tooltip title={t('Total filtered records')}>
					<Badge count={filteredData.length} size="small" color="#1677ff">
						<Tag className="cursor-default m-0" icon={<ThunderboltOutlined />}>{t('Records')}</Tag>
					</Badge>
				</Tooltip>
				{Object.keys(activeFilters).length > 0 && (
					<Tooltip title={t('Clear all filters')}>
						<Button size="small" icon={<ClearOutlined />} onClick={() => setActiveFilters({})} />
					</Tooltip>
				)}
				{graphMode === 'custom' && (
					<Button
						size="small"
						type="primary"
						icon={<PlusOutlined />}
						className="bg-blue-600"
						onClick={() => {
							// Quick add: use current builder config (or require open config)
							const cleanArr = (arr = []) => Array.from(new Set((arr || []).filter(Boolean)));
							const sorted = (arr = []) => [...arr].sort();
							const b = { ...builder, dimension: cleanArr(builder.dimension), series: cleanArr(builder.series) };
							setPanels(prev => {
								const idx = prev.findIndex(p => {
									const c = p.config;
									return c.chartType === b.chartType && c.dateField === b.dateField && c.topN === b.topN && (c.stack || 'none') === (b.stack || 'none') && JSON.stringify(sorted(c.series || [])) === JSON.stringify(sorted(b.series || []));
								});
								if (idx >= 0) {
									const next = [...prev];
									const cur = next[idx];
									const mergedDims = cleanArr([...(cur.config.dimension || []), ...(b.dimension || [])]);
									next[idx] = { ...cur, config: { ...cur.config, dimension: mergedDims } };
									return next;
								}
								return [...prev, { id: Date.now(), config: b, span: density === 'compact' ? 6 : 8 }];
							});
							message.success(t('Chart added'));
						}}
					>
						{t('Add Chart')}
					</Button>
				)}
				{/* Expand / collapse advanced config */}
				{graphMode === 'custom' && (
					<Tooltip title={controlsOpen ? 'Hide builder' : 'Show builder'}>
						<Button size="small" type="text" onClick={() => setControlsOpen(o => !o)} icon={controlsOpen ? <UpOutlined /> : <DownOutlined />} />
					</Tooltip>
				)}
			</div>

			{/* Active filters chips (inline to save space) */}
			{Object.keys(activeFilters).length > 0 && (
				<div className="flex flex-wrap gap-1 mt-3">
					{Object.entries(activeFilters).slice(0, 8).flatMap(([f, set]) => (
						Array.from(set).map(val => (
							<Tag
								key={`${f}:${val}`}
								closable
								onClose={() => removeFilter(f, val)}
								className="m-0 bg-blue-50 border-blue-200"
								icon={f === '__date__' ? <CalendarOutlined /> : <FilterOutlined />}
							>
								{f === '__date__' ? `Date: ${val}` : `${f}: ${val}`}
							</Tag>
						))
					))}
					{Object.entries(activeFilters).length > 8 && <Tag>+ more</Tag>}
				</div>
			)}

			{/* Collapsible advanced configuration (only custom mode) */}
			{graphMode === 'custom' && controlsOpen && (
				<div className="mt-4 border-t pt-4 space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
						<div>
							<label className="text-xs font-medium text-gray-600 flex items-center mb-1"><BarChartOutlined className="mr-1 text-indigo-500" />{t('Chart Type')}</label>
							<Select size="small" value={builder.chartType} className="w-full" options={[
								{ label: t('Pie'), value: 'Pie' },
								{ label: t('Donut'), value: 'Donut' },
								{ label: t('Bar'), value: 'Bar' },
								{ label: t('Column'), value: 'Column' },
								{ label: t('Line'), value: 'Line' },
								{ label: t('Area'), value: 'Area' },
								{ label: t('Scatter'), value: 'Scatter' },
								{ label: t('Rose'), value: 'Rose' },
							]} onChange={v => setBuilder(b => ({ ...b, chartType: v }))} />
						</div>
						<div>
							<label className="text-xs font-medium text-gray-600 flex items-center mb-1"><AppstoreOutlined className="mr-1 text-green-500" />{t('Dimensions')}</label>
							<Select size="small" mode="multiple" allowClear value={builder.dimension} className="w-full" options={fieldOptions.categorical} onChange={v => setBuilder(b => ({ ...b, dimension: v }))} tagRender={tagItemRender} placeholder={t('Dimensions')} />
						</div>
						<div>
							<label className="text-xs font-medium text-gray-600 flex items-center mb-1"><TeamOutlined className="mr-1 text-purple-500" />{t('Series')}</label>
							<Select size="small" mode="multiple" allowClear value={builder.series} className="w-full" options={fieldOptions.categorical} onChange={v => setBuilder(b => ({ ...b, series: v }))} tagRender={tagItemRender} placeholder={t('Series')} />
						</div>
						<div>
							<label className="text-xs font-medium text-gray-600 flex items-center mb-1"><CalendarOutlined className="mr-1 text-red-500" />{t('Date Field')}</label>
							<Select size="small" allowClear value={builder.dateField} className="w-full" options={fieldOptions.dates} onChange={v => setBuilder(b => ({ ...b, dateField: v }))} placeholder={t('Date field')} />
						</div>
						{builder.chartType === 'Scatter' && (
							<>
								<div>
									<label className="text-xs font-medium text-gray-600 flex items-center mb-1"><NumberOutlined className="mr-1 text-blue-500" />{t('X Axis')}</label>
									<Select size="small" value={builder.xField} className="w-full" options={fieldOptions.numeric} onChange={v => setBuilder(b => ({ ...b, xField: v }))} placeholder={t('X field')} />
								</div>
								<div>
									<label className="text-xs font-medium text-gray-600 flex items-center mb-1"><NumberOutlined className="mr-1 text-green-500" />{t('Y Axis')}</label>
									<Select size="small" value={builder.yField} className="w-full" options={fieldOptions.numeric} onChange={v => setBuilder(b => ({ ...b, yField: v }))} placeholder={t('Y field')} />
								</div>
							</>
						)}
						{['Bar', 'Column'].includes(builder.chartType) && (
							<div>
								<label className="text-xs font-medium text-gray-600 flex items-center mb-1"><BarChartOutlined className="mr-1 text-orange-500" />{t('Stack')}</label>
								<Select size="small" value={builder.stack || 'none'} className="w-full" options={[{ label: t('None'), value: 'none' }, { label: t('Stacked'), value: 'stacked' }, { label: '100% Stacked', value: 'percent' }]} onChange={v => setBuilder(b => ({ ...b, stack: v }))} />
							</div>
						)}
						<div>
							<label className="text-xs font-medium text-gray-600 flex items-center mb-1"><CalendarOutlined className="mr-1 text-pink-500" />{t('Date Range')}</label>
							<RangePicker size="small" value={dateRange} onChange={setDateRange} className="w-full" placeholder={['Start', 'End']} />
						</div>
						{(builder.series.length === 0 && ['Pie', 'Donut', 'Bar', 'Column', 'Rose'].includes(builder.chartType)) && (
							<div>
								<label className="text-xs font-medium text-gray-600 flex items-center mb-1"><TrophyOutlined className="mr-1 text-yellow-500" />{t('Top N')}</label>
								<InputNumber size="small" min={1} max={100} value={builder.topN} onChange={v => setBuilder(b => ({ ...b, topN: v || 10 }))} className="w-full" />
							</div>
						)}
					</div>
				</div>
			)}
		</Card>
	);

	// Helpers to build datasets and charts per panel/dimension
	const makeSeriesKey = (rec, seriesFields = []) => {
		if (!seriesFields || seriesFields.length === 0) return undefined;
		return seriesFields.map((f) => rec?.[f] ?? 'Unknown').join(' | ');
	};
	const buildDataset = (records, { chartType, dim, seriesFields = [], dateField, topN, xField, yField }) => {
		if (!records || records.length === 0) return [];
		const type = chartType;
		const map = new Map();
		if (type === 'Scatter') {
			const out = [];
			(records || []).forEach((rec) => {
				const x = rec?.[xField];
				const y = rec?.[yField];
				if (typeof x === 'number' && typeof y === 'number') {
					const sKey = seriesFields && seriesFields.length > 0 ? (rec?.[seriesFields[0]] ?? 'Unknown') : undefined;
					out.push({ x, y, ...(sKey ? { series: sKey } : {}) });
				}
			});
			return out;
		}
		if (type === 'Line' || type === 'Area') {
			(records || []).forEach((rec) => {
				const d = dateField && safeMoment(rec[dateField]).isValid() ? safeMoment(rec[dateField]).format('YYYY-MM-DD') : undefined;
				if (!d) return;
				const sKey = makeSeriesKey(rec, seriesFields);
				const key = sKey ? `${d}||${sKey}` : d;
				map.set(key, (map.get(key) || 0) + 1);
			});
			const out = [];
			map.forEach((val, k) => {
				if (k.includes('||')) {
					const [d, s] = k.split('||');
					out.push({ date: d, value: val, series: s });
				} else {
					out.push({ date: k, value: val });
				}
			});
			return out.sort((a, b) => new Date(a.date) - new Date(b.date));
		}
		(records || []).forEach((rec) => {
			const dval = (rec?.[dim] ?? 'Unknown');
			const sKey = makeSeriesKey(rec, seriesFields);
			const key = sKey ? `${dval}||${sKey}` : dval;
			map.set(key, (map.get(key) || 0) + 1);
		});
		let out = [];
		map.forEach((val, k) => {
			if (k.includes('||')) {
				const [d, s] = k.split('||');
				out.push({ [dim]: d, series: s, value: val });
			} else {
				out.push({ [dim]: k, value: val });
			}
		});
		if ((!seriesFields || seriesFields.length === 0) && topN && topN > 0 && (type === 'Pie' || type === 'Donut' || type === 'Bar' || type === 'Column' || type === 'Rose')) {
			out = out.sort((a, b) => b.value - a.value).slice(0, topN);
		}
		return out;
	};
	const renderChart = (cfg, dim, dataset, idKey) => {
		const type = cfg.chartType;
		const hasSeries = dataset.some((d) => d.series !== undefined);
		const chartHeight = density === 'compact' ? 240 : 300;
		const common = { legend: { position: 'bottom' }, height: chartHeight };
		const onReady = (plot) => {
			try { chartRefs.current.set(idKey, plot); } catch { }
			plot.on('element:click', (e) => {
				const datum = e?.data?.data;
				if (!datum) return;
				if (type === 'Line' || type === 'Area') {
					const v = datum.date;
					if (v) setActiveFilters((p) => ({ ...p, __date__: new Set([...(p.__date__ || []), v]) }));
				} else if (type === 'Scatter') {
					// No categorical field to add as a filter reliably
					return;
				} else {
					const v = datum[dim];
					if (v !== undefined) setActiveFilters((p) => ({ ...p, [dim]: new Set([...(p[dim] || []), v]) }));
				}
			});
		};
		if (type === 'Pie') {
			return (
				<AntPie data={dataset} angleField="value" colorField={dim} radius={0.9}
					label={{ text: (d) => `${d[dim] ?? 'Unknown'}: ${d.value ?? 0}` }}
					onReady={onReady}
					{...common}
				/>
			);
		}
		if (type === 'Donut') {
			return (
				<AntPie data={dataset} angleField="value" colorField={dim} radius={1} innerRadius={0.6}
					label={{ text: (d) => `${d[dim] ?? 'Unknown'}: ${d.value ?? 0}` }}
					onReady={onReady}
					{...common}
				/>
			);
		}
		if (type === 'Bar') {
			const stacked = cfg.stack === 'stacked' || cfg.stack === 'percent';
			const percent = cfg.stack === 'percent';
			return (
				<AntBar data={dataset} xField="value" yField={dim} {...(hasSeries ? { seriesField: 'series' } : {})}
					{...(stacked ? { isStack: true } : {})} {...(percent ? { isPercent: true } : {})}
					onReady={onReady} {...common} />
			);
		}
		if (type === 'Column') {
			const stacked = cfg.stack === 'stacked' || cfg.stack === 'percent';
			const percent = cfg.stack === 'percent';
			return (
				<AntColumn data={dataset} xField={dim} yField="value" {...(hasSeries ? { seriesField: 'series' } : {})}
					{...(stacked ? { isStack: true } : {})} {...(percent ? { isPercent: true } : {})}
					onReady={onReady} {...common} />
			);
		}
		if (type === 'Line') {
			return (
				<AntLine data={dataset} xField="date" yField="value" {...(hasSeries ? { seriesField: 'series' } : {})}
					point={{ size: 3 }} smooth onReady={onReady} {...common} />
			);
		}
		if (type === 'Area') {
			return (
				<AntArea data={dataset} xField="date" yField="value" {...(hasSeries ? { seriesField: 'series' } : {})}
					smooth onReady={onReady} {...common} />
			);
		}
		if (type === 'Scatter') {
			return (
				<AntScatter data={dataset} xField="x" yField="y" {...(hasSeries ? { colorField: 'series' } : {})}
					size={4} shape="circle" onReady={onReady} {...common} />
			);
		}
		if (type === 'Rose') {
			return (
				<AntRose data={dataset} xField={dim} yField="value" seriesField={hasSeries ? 'series' : undefined}
					radius={0.9} legend={{ position: 'bottom' }} onReady={onReady} {...common} />
			);
		}
		return null;
	};

	// Build columns for the viewer table based on dataset
	const buildViewerColumns = (chartType, dim, dataset) => {
		const hasSeries = dataset.some((d) => d.series !== undefined);
		const cols = [];
		if (chartType === 'Line' || chartType === 'Area') {
			cols.push({ title: 'date', dataIndex: 'date', key: 'date' });
		} else if (chartType === 'Scatter') {
			cols.push({ title: 'x', dataIndex: 'x', key: 'x' });
			cols.push({ title: 'y', dataIndex: 'y', key: 'y' });
		} else {
			cols.push({ title: dim, dataIndex: dim, key: dim });
		}
		if (hasSeries) cols.push({ title: 'series', dataIndex: 'series', key: 'series' });
		if (chartType !== 'Scatter') cols.push({ title: 'value', dataIndex: 'value', key: 'value' });
		return cols;
	};

	return (
		<div className="space-y-3">
			{BuilderControls}
			{(filteredData?.length || 0) === 0 ? (
				<Empty description={t('No data found')} className="py-8" />
			) : (graphMode === 'overview' ? (
				<>
					{/* Enhanced KPI strip with better styling */}
					<KpiStrip data={filteredData} dateField={builder.dateField} density={density} />

					{/* Main Charts Grid with enhanced cards */}
					<Row gutter={density === 'compact' ? [24, 24] : [32, 32]} className="mt-6">
						<Col xs={24} xl={12}>
							<Card
								className="shadow-lg border-blue-200 hover:shadow-xl transition-all duration-300"
								bodyStyle={{ padding: density === 'compact' ? 20 : 24 }}
								title={
									<div className="flex items-center space-x-3">
										<div className="p-2 bg-blue-100 rounded-lg">
											<PieChartOutlined className="text-blue-600" />
										</div>
										<div>
											<div className="font-semibold text-gray-800">{t('Status Distribution')}</div>
											<div className="text-xs text-gray-500">{t('Click segments to filter')}</div>
										</div>
									</div>
								}
								extra={
									<Tooltip title={t('View detailed breakdown')}>
										<Button size="small" icon={<EyeOutlined />} type="text" />
									</Tooltip>
								}
							>
								{ds && ds.status && ds.status.length > 0 ? (
									<AntPie
										data={ds.status}
										angleField="value"
										colorField="status"
										radius={0.9}
										label={{
											text: (d) => `${d.status ?? 'Unknown'}: ${d.value ?? 0}`,
											style: { fontWeight: 'bold' }
										}}
										legend={{ position: 'bottom' }}
										onReady={(plot) => plot.on('element:click', (e) => {
											const v = e?.data?.data?.status;
											if (v !== undefined) {
												setActiveFilters(p => ({ ...p, status: new Set([...(p.status || []), v]) }));
												message.success(`Filtered by status: ${v}`);
											}
										})}
										height={density === 'compact' ? 260 : 320}
										interactions={[{ type: 'element-highlight' }]}
									/>
								) : (
									<Empty description={t('No status data available')} />
								)}
							</Card>
						</Col>

						<Col xs={24} xl={12}>
							<Card
								className="shadow-lg border-green-200 hover:shadow-xl transition-all duration-300"
								bodyStyle={{ padding: density === 'compact' ? 20 : 24 }}
								title={
									<div className="flex items-center space-x-3">
										<div className="p-2 bg-green-100 rounded-lg">
											<LineChartOutlined className="text-green-600" />
										</div>
										<div>
											<div className="font-semibold text-gray-800">{t('Requests Per Day')}</div>
											<div className="text-xs text-gray-500">{t('Trend over time')}</div>
										</div>
									</div>
								}
								extra={
									<Space>
										<Tooltip title={t('Show trend analysis')}>
											<Button size="small" icon={<RiseOutlined />} type="text" />
										</Tooltip>
										<Tooltip title={t('Download chart')}>
											<Button size="small" icon={<DownloadOutlined />} type="text" />
										</Tooltip>
									</Space>
								}
							>
								{ds && ds.dates && ds.dates.length > 0 ? (
									<AntLine
										data={ds.dates}
										xField="date"
										yField="value"
										point={{ size: 4, shape: 'circle' }}
										smooth
										onReady={(plot) => plot.on('element:click', (e) => {
											const d = e?.data?.data?.date;
											if (d) {
												setActiveFilters(p => ({ ...p, __date__: new Set([...(p.__date__ || []), d]) }));
												message.success(`Filtered by date: ${d}`);
											}
										})}
										height={density === 'compact' ? 260 : 320}
										color="#10b981"
										interactions={[{ type: 'marker-active' }]}
									/>
								) : (
									<Empty description={t('No date data available')} />
								)}
							</Card>
						</Col>
					</Row>

					<Row gutter={density === 'compact' ? [24, 24] : [32, 32]} className="mt-6">
						<Col xs={24} xl={12}>
							<Card
								className="shadow-lg border-purple-200 hover:shadow-xl transition-all duration-300"
								bodyStyle={{ padding: density === 'compact' ? 20 : 24 }}
								title={
									<div className="flex items-center space-x-3">
										<div className="p-2 bg-purple-100 rounded-lg">
											<TeamOutlined className="text-purple-600" />
										</div>
										<div>
											<div className="font-semibold text-gray-800">{t('Top Requesters')}</div>
											<div className="text-xs text-gray-500">{t('Most active users')}</div>
										</div>
									</div>
								}
								extra={
									<Tooltip title={t('View all requesters')}>
										<Button size="small" icon={<TeamOutlined />} type="text" />
									</Tooltip>
								}
							>
								{ds && ds.requesters && ds.requesters.length > 0 ? (
									<AntBar
										data={ds.requesters}
										xField="value"
										yField="requester"
										seriesField="requester"
										legend={false}
										onReady={(plot) => plot.on('element:click', (e) => {
											const v = e?.data?.data?.requester;
											if (v) {
												setActiveFilters(p => ({ ...p, requester: new Set([...(p.requester || []), v]) }));
												message.success(`Filtered by requester: ${v}`);
											}
										})}
										height={density === 'compact' ? 260 : 320}
										color="#8b5cf6"
										interactions={[{ type: 'element-highlight' }]}
									/>
								) : (
									<Empty description={t('No requester data available')} />
								)}
							</Card>
						</Col>

						<Col xs={24} xl={12}>
							<Card
								className="shadow-lg border-orange-200 hover:shadow-xl transition-all duration-300"
								bodyStyle={{ padding: density === 'compact' ? 20 : 24 }}
								title={
									<div className="flex items-center space-x-3">
										<div className="p-2 bg-orange-100 rounded-lg">
											<BarChartOutlined className="text-orange-600" />
										</div>
										<div>
											<div className="font-semibold text-gray-800">{t('Cycle Distribution')}</div>
											<div className="text-xs text-gray-500">{t('Workflow cycles')}</div>
										</div>
									</div>
								}
								extra={
									<Tooltip title={t('Cycle analysis')}>
										<Button size="small" icon={<SyncOutlined />} type="text" />
									</Tooltip>
								}
							>
								{ds && ds.cycles && ds.cycles.length > 0 ? (
									<AntColumn
										data={ds.cycles}
										xField="cycle"
										yField="value"
										onReady={(plot) => plot.on('element:click', (e) => {
											const v = e?.data?.data?.cycle;
											if (v !== undefined) {
												setActiveFilters(p => ({ ...p, cycle: new Set([...(p.cycle || []), v]) }));
												message.success(`Filtered by cycle: ${v}`);
											}
										})}
										height={density === 'compact' ? 260 : 320}
										color="#f59e0b"
										interactions={[{ type: 'element-highlight' }]}
									/>
								) : (
									<Empty description={t('No cycle data available')} />
								)}
							</Card>
						</Col>
					</Row>
				</>
			) : (
				<>
					{panels.length === 0 ? (
						<div className="text-center py-16">
							<Empty
								description={
									<div className="space-y-4">
										<div className="text-lg font-medium text-gray-600">{t('No Custom Charts Created')}</div>
										<div className="text-sm text-gray-400 max-w-md mx-auto">
											{t('Configure your chart settings above and click "Add Chart to Dashboard" to create custom visualizations')}
										</div>
									</div>
								}
								image={<BarChartOutlined className="text-6xl text-gray-300" />}
							>
								<Button
									type="primary"
									size="large"
									icon={<PlusOutlined />}
									className="bg-gradient-to-r from-blue-500 to-blue-600 border-0 shadow-lg"
									onClick={() => {
										// Auto-create a sample chart
										const sampleConfig = {
											chartType: 'Pie',
											dimension: fieldOptions.defaults.dimDefault ? [fieldOptions.defaults.dimDefault] : [],
											series: [],
											dateField: fieldOptions.defaults.dateDefault,
											topN: 10
										};
										setPanels([{ id: Date.now(), config: sampleConfig, span: 8 }]);
										message.success(t('Sample chart created!'));
									}}
								>
									{t('Create Sample Chart')}
								</Button>
							</Empty>
						</div>
					) : (
						<>
							{/* Custom Charts Header */}
							{/* <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 mb-6 border border-indigo-200">
								<div className="flex items-center justify-between">
									<div className="flex items-center space-x-3">
										<div className="p-2 bg-indigo-100 rounded-lg">
											<AppstoreOutlined className="text-indigo-600 text-lg" />
										</div>
										<div>
											<div className="text-lg font-semibold text-gray-800">Custom Dashboard</div>
											<div className="text-sm text-gray-500">
												{panels.length} chart{panels.length !== 1 ? 's' : ''} • Click charts to filter data
											</div>
										</div>
									</div>
									<Space>
										<Badge count={panels.length} className="mr-2">
											<Button icon={<AppstoreOutlined />} size="small">Charts</Button>
										</Badge>
										<Tooltip title="Clear all charts">
											<Button 
												icon={<ClearOutlined />} 
												size="small" 
												danger 
												type="text"
												onClick={() => {
													Modal.confirm({
														title: 'Clear All Charts',
														content: 'Are you sure you want to remove all charts from the dashboard?',
														onOk: () => {
															setPanels([]);
															message.success('All charts cleared!');
														}
													});
												}}
											>
												Clear All
											</Button>
										</Tooltip>
									</Space>
								</div>
							</div> */}

							{/* Charts Grid */}
							<Row gutter={density === 'compact' ? [24, 24] : [32, 32]}>
								{panels.reduce((acc, panel) => {
									const cfg = panel.config;
									const dims = (cfg.chartType === 'Line' || cfg.chartType === 'Area' || cfg.chartType === 'Scatter') ? [cfg.chartType === 'Scatter' ? (cfg.dimension?.[0] || 'value') : undefined] : ((cfg.dimension && cfg.dimension.length > 0) ? cfg.dimension : ['status']);
									dims.forEach((dim) => {
										const dataset = buildDataset(filteredData, { chartType: cfg.chartType, dim, seriesFields: cfg.series || [], dateField: cfg.dateField, topN: cfg.topN, xField: cfg.xField, yField: cfg.yField });
										const title = `${cfg.chartType}: ${dim || (cfg.dateField || (cfg.chartType === 'Scatter' ? `${cfg.xField} vs ${cfg.yField}` : 'date'))}${(cfg.series && cfg.series.length > 0) ? ' by ' + cfg.series.join(' | ') : ''}`;
										const idKey = `${panel.id}:${dim || 'date'}`;
										const span = panel.span || 8;

										// Chart type icon mapping
										const getChartIcon = (chartType) => {
											switch (chartType) {
												case 'Pie':
												case 'Donut': return <PieChartOutlined className="text-blue-500" />;
												case 'Bar':
												case 'Column': return <BarChartOutlined className="text-green-500" />;
												case 'Line': return <LineChartOutlined className="text-purple-500" />;
												case 'Area': return <AreaChartOutlined className="text-orange-500" />;
												case 'Scatter': return <DotChartOutlined className="text-red-500" />;
												case 'Rose': return <RiseOutlined className="text-pink-500" />;
												default: return <BarChartOutlined className="text-gray-500" />;
											}
										};

										acc.push(
											<Col xs={24} md={span >= 24 ? 24 : 12} xl={span} key={idKey}>
												<Card
													className="shadow-lg border-gray-200 hover:shadow-xl transition-all duration-300 h-full"
													bodyStyle={{ padding: density === 'compact' ? 16 : 20 }}
													title={
														<div className="flex items-center space-x-3">
															<div className="p-1.5 bg-gray-100 rounded-lg">
																{getChartIcon(cfg.chartType)}
															</div>
															<div className="flex-1 min-w-0">
																<div className="font-semibold text-gray-800 truncate">{title}</div>
																<div className="text-xs text-gray-500">
																	{dataset.length} {t('data point')}{dataset.length !== 1 ? 's' : ''}
																</div>
															</div>
														</div>
													}
													extra={
														<Space size={4}>
															<Tooltip title={t('View data')}>
																<Button
																	size="small"
																	type="text"
																	icon={<EyeOutlined />}
																	onClick={() => {
																		const cols = buildViewerColumns(cfg.chartType, dim || 'date', dataset);
																		setViewer({ open: true, title, columns: cols, data: dataset.map((r, i) => ({ key: i, ...r })) });
																	}}
																/>
															</Tooltip>
															<Dropdown
																menu={{
																	items: [
																		{ key: 'csv', label: t('Export CSV'), icon: <DownloadOutlined /> },
																		{ key: 'png', label: t('Export PNG'), icon: <DownloadOutlined /> },
																	],
																	onClick: ({ key }) => {
																		if (key === 'csv') {
																			const headers = Object.keys(dataset[0] || {});
																			const rows = dataset.map((r) => headers.map((h) => (r[h] ?? '')).join(','));
																			const csv = [headers.join(','), ...rows].join('\n');
																			const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
																			const a = document.createElement('a');
																			a.href = URL.createObjectURL(blob);
																			a.download = `${title.replace(/\s+/g, '_')}.csv`;
																			a.click();
																			URL.revokeObjectURL(a.href);
																			message.success(t('CSV exported successfully!'));
																		} else if (key === 'png') {
																			try {
																				const plot = chartRefs.current.get(idKey);
																				const dataUrl = (plot?.chart?.getCanvas?.().get('el')?.toDataURL?.('image/png')) || (plot?.toDataURL?.());
																				if (!dataUrl) throw new Error('PNG export unsupported');
																				const a = document.createElement('a');
																				a.href = dataUrl;
																				a.download = `${title.replace(/\s+/g, '_')}.png`;
																				a.click();
																				message.success(t('PNG exported successfully!'));
																			} catch (err) {
																				message.error(t('PNG export not supported in this environment'));
																			}
																		}
																	}
																}}
															>
																<Button size="small" type="text" icon={<DownloadOutlined />} />
															</Dropdown>
															<Dropdown
																menu={{
																	items: [
																		{ key: '6', label: '1/4 Width', icon: <CompressOutlined /> },
																		{ key: '8', label: '1/3 Width', icon: <CompressOutlined /> },
																		{ key: '12', label: '1/2 Width', icon: <ExpandOutlined /> },
																		{ key: '24', label: t('Full Width'), icon: <FullscreenOutlined /> },
																	],
																	onClick: ({ key }) => {
																		setPanels((prev) => prev.map((p) => p.id === panel.id ? { ...p, span: Number(key) } : p));
																		message.success(`Chart resized to ${key === '6' ? '1/4' : key === '8' ? '1/3' : key === '12' ? '1/2' : 'full'} width`);
																	},
																}}
															>
																<Button size="small" type="text" icon={span === 24 ? <FullscreenExitOutlined /> : <FullscreenOutlined />} />
															</Dropdown>
															<Tooltip title={t('Remove chart')}>
																<Button
																	size="small"
																	type="text"
																	danger
																	icon={<DeleteOutlined />}
																	onClick={() => {
																		setPanels(prev => prev.filter(p => p.id !== panel.id));
																		message.success(t('Chart removed!'));
																	}}
																/>
															</Tooltip>
														</Space>
													}
												>
													{dataset.length > 0 ?
														renderChart({ chartType: cfg.chartType, stack: cfg.stack }, dim || 'date', dataset, idKey) :
														<Empty description={t('No data available for this configuration')} />
													}
												</Card>
											</Col>
										);
									});
									return acc;
								}, [])}
							</Row>
						</>
					)}
				</>
			))}
			<Modal
				open={viewer.open}
				title={
					<div className="flex items-center space-x-3">
						<div className="p-2 bg-blue-100 rounded-lg">
							<EyeOutlined className="text-blue-600" />
						</div>
						<div>
							<div className="font-semibold text-gray-800">{viewer.title || 'Chart Data'}</div>
							<div className="text-sm text-gray-500">{t('Detailed view of chart data')}</div>
						</div>
					</div>
				}
				width={900}
				footer={
					<div className="flex items-center justify-between">
						<div className="text-sm text-gray-500">
							{viewer.data?.length || 0} record{(viewer.data?.length || 0) !== 1 ? 's' : ''}
						</div>
						<Space>
							<Button
								icon={<DownloadOutlined />}
								onClick={() => {
									if (viewer.data && viewer.data.length > 0) {
										const headers = Object.keys(viewer.data[0] || {}).filter(k => k !== 'key');
										const rows = viewer.data.map((r) => headers.map((h) => (r[h] ?? '')).join(','));
										const csv = [headers.join(','), ...rows].join('\n');
										const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
										const a = document.createElement('a');
										a.href = URL.createObjectURL(blob);
										a.download = `chart_data_${Date.now()}.csv`;
										a.click();
										URL.revokeObjectURL(a.href);
										message.success(t('Data exported successfully!'));
									}
								}}
							>
								{t('Export CSV')}
							</Button>
							<Button
								type="primary"
								onClick={() => setViewer({ open: false, title: '', columns: [], data: [] })}
							>
								{t('Close')}
							</Button>
						</Space>
					</div>
				}
				onCancel={() => setViewer({ open: false, title: '', columns: [], data: [] })}
				destroyOnHidden
				className="top-8"
			>
				<div className="border border-gray-200 rounded-lg overflow-hidden">
					<Table
						columns={viewer.columns}
						dataSource={viewer.data}
						size="small"
						pagination={{
							pageSize: 20,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
						}}
						scroll={{ x: true, y: 400 }}
						className="custom-table-modal"
					/>
				</div>
			</Modal>
		</div>
	);
};

// View modal for dataset

/* eslint-disable react/prop-types */
// Keep modal outside the component export to avoid re-mount issues
// Rendered within GraphicalDashboard's return below using the `viewer` state


export default TableComponent;

// Enhanced KPI strip with better styling and icons
const KpiStrip = ({ data = [], dateField, density = 'compact' }) => {
	const { t } = useTranslation();
	const total = data.length;
	const closedWords = ['close', 'closed', 'resolved', 'done', 'complete', 'completed'];
	const statusCounts = data.reduce((acc, r) => { const s = (r.status ?? r.Status ?? 'Unknown'); acc[s] = (acc[s] || 0) + 1; return acc; }, {});
	const closed = Object.entries(statusCounts).reduce((n, [k, v]) => n + (closedWords.some(w => `${k}`.toLowerCase().includes(w)) ? v : 0), 0);
	const open = total - closed;
	const today = dateField ? data.filter(r => safeMoment(r[dateField]).isValid() && safeMoment(r[dateField]).isSame(moment(), 'day')).length : 0;
	const last7 = dateField ? data.filter(r => safeMoment(r[dateField]).isValid() && safeMoment(r[dateField]).isSameOrAfter(moment().subtract(6, 'day'), 'day')).length : 0;

	const CardKpi = ({ title, value, sub, icon, color, trend }) => (
		<Card
			className={`shadow-lg border-${color}-200 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-${color}-50`}
			bodyStyle={{ padding: density === 'compact' ? 20 : 24 }}
		>
			<div className="flex items-center justify-between">
				<div className="flex-1">
					<div className="flex items-center space-x-3 mb-3">
						<div className={`p-2 bg-${color}-100 rounded-lg`}>
							{icon}
						</div>
						<div className={`text-sm text-${color}-600 uppercase tracking-wide font-semibold`}>
							{title}
						</div>
					</div>
					<div className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 break-all leading-tight mb-2">
						{value.toLocaleString()}
					</div>
					{sub !== undefined && (
						<div className="flex items-center space-x-2">
							<div className="text-sm text-gray-500">{sub}</div>
							{trend && (
								<div className={`text-xs px-2 py-1 rounded-full ${trend > 0 ? 'bg-green-100 text-green-700' : trend < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
									{trend > 0 ? '↗' : trend < 0 ? '↘' : '→'} {Math.abs(trend)}%
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</Card>
	);

	return (
		<Row gutter={density === 'compact' ? [20, 20] : [24, 24]} className="mb-6">
			<Col xs={12} sm={6}>
				<CardKpi
					title={t('Total Records')}
					value={total}
					icon={<NumberOutlined className="text-blue-600 text-lg" />}
					color="blue"
				/>
			</Col>
			<Col xs={12} sm={6}>
				<CardKpi
					title={t('Open Items')}
					value={open}
					sub={`Closed: ${closed.toLocaleString()}`}
					icon={<SyncOutlined className="text-orange-600 text-lg" />}
					color="orange"
				/>
			</Col>
			<Col xs={12} sm={6}>
				<CardKpi
					title={t('Today')}
					value={today}
					sub={dateField ? moment().format('DD MMM YYYY') : 'No date field'}
					icon={<ClockCircleOutlined className="text-green-600 text-lg" />}
					color="green"
				/>
			</Col>
			<Col xs={12} sm={6}>
				<CardKpi
					title={t('Last 7 Days')}
					value={last7}
					sub={`${((last7 / total) * 100).toFixed(1)}% of total`}
					icon={<CheckCircleOutlined className="text-purple-600 text-lg" />}
					color="purple"
				/>
			</Col>
		</Row>
	);
};

